import { SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  Button,
  Dialog,
  Flex,
  IconButton,
  PillToggle,
  Separator,
  Slider,
  Switch,
  Text,
} from '@/components/common';
import {
  DISCOVERY_AGE_MAX,
  DISCOVERY_AGE_MIN,
  DISCOVERY_DISTANCE_MAX,
  DISCOVERY_DISTANCE_MIN,
  DISCOVERY_INTENTION_OPTIONS,
  DISCOVERY_OPEN_TO_OPTIONS,
  DISCOVERY_RELATIONSHIP_STATUS_OPTIONS,
  type DiscoveryEthnicityMode,
  type DiscoveryGenderMode,
} from '@/constants/discoveryFilters';
import { useCurrentUser } from '@/contexts';
import { useSubscription } from '@/hooks';
import { useFilterStore, type PreferencesSnapshot } from '@/stores/filterStore';

type Draft = PreferencesSnapshot;

function snapshotFromStore(): Draft {
  const s = useFilterStore.getState();
  return {
    filterDistance: s.filterDistance,
    filterMinAge: s.filterMinAge,
    filterMaxAge: s.filterMaxAge,
    availableToday: s.availableToday,
    intentions: [...s.intentions],
    openTo: [...s.openTo],
    relationshipStatus: [...s.relationshipStatus],
    genderMode: s.genderMode,
    ethnicityMode: s.ethnicityMode,
  };
}

const defaultsSnapshot = (): Draft => ({
  filterDistance: DISCOVERY_DISTANCE_MAX,
  filterMinAge: DISCOVERY_AGE_MIN,
  filterMaxAge: 100,
  availableToday: false,
  intentions: [],
  openTo: [],
  relationshipStatus: [],
  genderMode: 'all',
  ethnicityMode: 'all',
});

function optionLabel(
  opt: { id: string; label?: string; labelKey?: string },
  t: (key: string) => string
): string {
  return 'label' in opt && opt.label != null ? opt.label : t(opt.labelKey!);
}

function idsToLabels(
  ids: string[],
  options: readonly { id: string; label?: string; labelKey?: string }[],
  t: (key: string) => string
) {
  return ids
    .map(id => {
      const opt = options.find(o => o.id === id);
      return opt ? optionLabel(opt, t) : null;
    })
    .filter((v): v is string => Boolean(v));
}

function labelsToIds(
  labels: string[],
  options: readonly { id: string; label?: string; labelKey?: string }[],
  t: (key: string) => string
) {
  return labels
    .map(label => {
      const opt = options.find(o => optionLabel(o, t) === label);
      return opt?.id;
    })
    .filter((v): v is string => Boolean(v));
}

export function DiscoveryFilterDialog() {
  const { t } = useTranslation('discover');
  const { currentUser, settings } = useCurrentUser();
  const { presentPaywall } = useSubscription();
  const applyPreferences = useFilterStore(s => s.applyPreferences);
  const [draft, setDraft] = useState<Draft>(snapshotFromStore);
  const hasSubscription = currentUser?.subscription?.active ?? false;
  const canFilterByAvailableToday = currentUser?.availableToday ?? false;
  const canFilterByDistance = settings?.locationAllowed ?? false;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      const s = snapshotFromStore();
      setDraft({
        ...s,
        availableToday: canFilterByAvailableToday && s.availableToday,
        filterDistance: canFilterByDistance ? s.filterDistance : DISCOVERY_DISTANCE_MAX,
      });
    }
  };

  const intentionLabels = DISCOVERY_INTENTION_OPTIONS.map(o => t(o.labelKey));
  const openToLabels = DISCOVERY_OPEN_TO_OPTIONS.map(o => o.label);
  const relationshipLabels = DISCOVERY_RELATIONSHIP_STATUS_OPTIONS.map(o => o.label);

  const genderOptions = [t('filters.gender.all'), t('filters.gender.mine')];
  const ethnicityOptions = [t('filters.ethnicity.all'), t('filters.ethnicity.mine')];

  const sectionLabel = (text: string) => (
    <Text size="1" weight="medium" className="text-white">
      {text}
    </Text>
  );

  const filterButton = (
    <IconButton
      type="button"
      variant="outline"
      color="gray"
      highContrast
      radius="full"
      size="2"
      aria-label={t('header.openFilters')}
      onClick={hasSubscription ? undefined : () => void presentPaywall().catch(() => {})}
    >
      <SlidersHorizontal size={20} aria-hidden />
    </IconButton>
  );

  if (!hasSubscription) {
    return filterButton;
  }

  return (
    <Dialog.Root onOpenChange={handleOpenChange} variant="fullscreen">
      <Dialog.Trigger>{filterButton}</Dialog.Trigger>
      <Dialog.Content aria-describedby={undefined} style={{ overflow: 'hidden' }}>
        <Dialog.Title
          backButton
          endSlot={
            <Button
              type="button"
              variant="outline"
              color="gray"
              highContrast
              radius="full"
              size="sm"
              onClick={() => setDraft(defaultsSnapshot())}
            >
              {t('filters.reset')}
            </Button>
          }
        >
          {t('filters.title')}
        </Dialog.Title>
        <Dialog.Body>
          <Flex direction="column" gap="5">
            <Flex direction="column" gap="2">
              <Flex justify="between" align="center">
                <Flex direction="column" gap="1">
                  {sectionLabel(t('filters.distance.label'))}
                  {!canFilterByDistance && (
                    <Text size="1" color="gray">
                      {t('filters.distance.hint')}
                    </Text>
                  )}
                </Flex>
                <Text size="1">
                  {t('filters.distance.value', { distance: draft.filterDistance })}
                </Text>
              </Flex>
              <Slider
                value={[draft.filterDistance]}
                min={DISCOVERY_DISTANCE_MIN}
                max={DISCOVERY_DISTANCE_MAX}
                step={1}
                disabled={!canFilterByDistance}
                onValueChange={([val]) =>
                  canFilterByDistance ? setDraft(d => ({ ...d, filterDistance: val })) : undefined
                }
                aria-label={t('filters.distance.label')}
              />
            </Flex>

            <Flex direction="column" gap="2">
              <Flex justify="between" align="center">
                {sectionLabel(t('filters.age.label'))}
                <Text size="1">
                  {t('filters.age.value', { min: draft.filterMinAge, max: draft.filterMaxAge })}
                </Text>
              </Flex>
              <Slider
                value={[draft.filterMinAge, draft.filterMaxAge]}
                min={DISCOVERY_AGE_MIN}
                max={DISCOVERY_AGE_MAX}
                step={1}
                onValueChange={([min, max]) =>
                  setDraft(d => ({
                    ...d,
                    filterMinAge: Math.min(min, max),
                    filterMaxAge: Math.max(min, max),
                  }))
                }
                aria-label={t('filters.age.label')}
              />
            </Flex>

            <Separator size="4" color="red" />

            <Flex align="center" justify="between">
              <Flex direction="column" gap="1">
                {sectionLabel(t('filters.availableToday.label'))}
                {!canFilterByAvailableToday && (
                  <Text size="1" color="gray">
                    {t('filters.availableToday.hint')}
                  </Text>
                )}
              </Flex>
              <Switch
                checked={canFilterByAvailableToday && draft.availableToday}
                disabled={!canFilterByAvailableToday}
                onCheckedChange={checked =>
                  setDraft(d => ({ ...d, availableToday: checked === true }))
                }
                aria-label={t('filters.availableToday.label')}
              />
            </Flex>

            <Separator size="4" color="red" />

            <Flex direction="column" gap="2">
              {sectionLabel(t('filters.intentions.label'))}
              <PillToggle
                type="multiple"
                options={intentionLabels}
                value={idsToLabels(draft.intentions, DISCOVERY_INTENTION_OPTIONS, t)}
                onValueChange={labels =>
                  setDraft(d => ({
                    ...d,
                    intentions: labelsToIds(labels, DISCOVERY_INTENTION_OPTIONS, t),
                  }))
                }
              />
            </Flex>

            <Flex direction="column" gap="2">
              {sectionLabel(t('filters.openTo.label'))}
              <PillToggle
                type="multiple"
                options={openToLabels}
                value={idsToLabels(draft.openTo, DISCOVERY_OPEN_TO_OPTIONS, t)}
                onValueChange={labels =>
                  setDraft(d => ({
                    ...d,
                    openTo: labelsToIds(labels, DISCOVERY_OPEN_TO_OPTIONS, t),
                  }))
                }
              />
            </Flex>

            <Flex direction="column" gap="2">
              {sectionLabel(t('filters.relationshipStatus.label'))}
              <PillToggle
                type="multiple"
                options={relationshipLabels}
                value={idsToLabels(
                  draft.relationshipStatus,
                  DISCOVERY_RELATIONSHIP_STATUS_OPTIONS,
                  t
                )}
                onValueChange={labels =>
                  setDraft(d => ({
                    ...d,
                    relationshipStatus: labelsToIds(
                      labels,
                      DISCOVERY_RELATIONSHIP_STATUS_OPTIONS,
                      t
                    ),
                  }))
                }
              />
            </Flex>

            <Flex direction="column" gap="2">
              {sectionLabel(t('filters.gender.label'))}
              <PillToggle
                type="single"
                options={genderOptions}
                value={draft.genderMode === 'mine' ? genderOptions[1] : genderOptions[0]}
                onValueChange={value => {
                  const mode: DiscoveryGenderMode = value === genderOptions[1] ? 'mine' : 'all';
                  setDraft(d => ({ ...d, genderMode: mode }));
                }}
              />
            </Flex>

            <Flex direction="column" gap="2" pb="4">
              {sectionLabel(t('filters.ethnicity.label'))}
              <PillToggle
                type="single"
                options={ethnicityOptions}
                value={draft.ethnicityMode === 'mine' ? ethnicityOptions[1] : ethnicityOptions[0]}
                onValueChange={value => {
                  const mode: DiscoveryEthnicityMode =
                    value === ethnicityOptions[1] ? 'mine' : 'all';
                  setDraft(d => ({ ...d, ethnicityMode: mode }));
                }}
              />
            </Flex>
          </Flex>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Close>
            <Button
              type="button"
              color="gray"
              highContrast
              size="lg"
              fullWidth
              onClick={() => {
                applyPreferences({
                  ...draft,
                  availableToday: canFilterByAvailableToday && draft.availableToday,
                  filterDistance: canFilterByDistance
                    ? draft.filterDistance
                    : DISCOVERY_DISTANCE_MAX,
                });
              }}
            >
              {t('filters.update')}
            </Button>
          </Dialog.Close>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
